import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, CATEGORY_LABEL } from "@/lib/categories";

export default function Apply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    course_slug: params.get("kurs") || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.course_slug) {
      toast.error("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", form);
      setDone(true);
      toast.success("Başvurunuz alındı!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    items: courses.filter((c) => c.category === cat.id),
  }));

  if (done) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="apply-success">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="font-display text-4xl tracking-tight mb-4">Başvurunuz alındı</h1>
          <p className="text-neutral-500 mb-8">
            Uzman danışmanımız en geç 24 saat içinde sizinle iletişime geçecek.
          </p>
          <button onClick={() => navigate("/")} data-testid="back-home-btn" className="btn-accent">
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page">
      <section className="pt-24 md:pt-32 pb-8 max-w-3xl mx-auto px-6 md:px-10">
        <div className="overline text-[color:var(--ek-accent)] mb-4">Başvuru Formu</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[0.95] mb-4">
          Bir adım. Bir gelecek.
        </h1>
        <p className="text-lg text-neutral-500">
          Formu doldurun; danışmanımız 24 saat içinde arasın.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <form onSubmit={submit} className="space-y-6" data-testid="apply-form">
          <Field label="Ad Soyad *" testId="input-name">
            <input
              value={form.full_name}
              onChange={set("full_name")}
              type="text"
              required
              className="input"
              data-testid="input-name"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Telefon *" testId="input-phone">
              <input
                value={form.phone}
                onChange={set("phone")}
                type="tel"
                required
                placeholder="0555 000 00 00"
                className="input"
                data-testid="input-phone"
              />
            </Field>
            <Field label="E-posta *" testId="input-email">
              <input
                value={form.email}
                onChange={set("email")}
                type="email"
                required
                className="input"
                data-testid="input-email"
              />
            </Field>
          </div>
          <Field label="İlgilendiğim Kurs *" testId="select-course">
            <select
              value={form.course_slug}
              onChange={set("course_slug")}
              required
              className="input"
              data-testid="select-course"
            >
              <option value="">Kurs seçin…</option>
              {grouped.map((g) => (
                <optgroup key={g.id} label={CATEGORY_LABEL[g.id]}>
                  {g.items.map((c) => (
                    <option key={c.id} value={c.slug}>{c.title}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          <Field label="Mesajınız (opsiyonel)" testId="input-message">
            <textarea
              value={form.message}
              onChange={set("message")}
              rows={4}
              className="input resize-none"
              data-testid="input-message"
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-application-btn"
            className="btn-accent w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Gönderiliyor…" : "Başvuruyu Gönder"}
          </button>
          <p className="text-xs text-neutral-500 text-center">
            KVKK kapsamında verileriniz yalnızca kayıt sürecinde kullanılır.
          </p>
        </form>
      </section>

      <style>{`
        .input {
          width: 100%;
          padding: 14px 16px;
          border-radius: 12px;
          background: #F5F5F7;
          border: 1px solid transparent;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          transition: border-color 200ms, background 200ms;
          outline: none;
        }
        .input:focus {
          border-color: #0A0A0A;
          background: #fff;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-neutral-700 mb-2">{label}</span>
      {children}
    </label>
  );
}
